import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, Search, Trash2, Edit3, Users, CreditCard, Activity, Mail } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E4DFD2] rounded-lg px-3 py-2 text-sm text-[#1F2326] placeholder-[#A9A294] focus:outline-none focus:border-[#1F2326] focus:ring-1 focus:ring-[#1F2326] transition-colors";

const statusTone = {
  paid: "bg-[#EFF3EE] text-[#42624B] border-[#C5D5C8]",
  trial: "bg-[#F0EADA] text-[#7A5E1F] border-[#E4DFD2]",
  trial_expired: "bg-[#FAEEED] text-[#7E332D] border-[#E7BFBA]",
  cancelled: "bg-[#F0EFEC] text-[#69747C] border-[#E4DFD2]",
  inactive: "bg-[#F0EFEC] text-[#69747C] border-[#E4DFD2]",
};

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return s;
  }
};

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [tab, setTab] = useState("users");

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [resendingUserId, setResendingUserId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await api.get("/admin/me");
        if (r.data.is_admin) {
          setAuthorized(true);
        } else {
          setAuthorized(false);
        }
      } catch {
        setAuthorized(false);
      }
    })();
  }, [user, authLoading, navigate]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const r = await api.get("/admin/users", { params: search ? { q: search } : {} });
      setUsers(r.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const r = await api.get("/admin/analytics");
      setAnalytics(r.data);
    } catch {
      toast.error("Failed to load analytics");
    }
  };

  useEffect(() => {
    if (authorized) {
      loadUsers();
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized]);

  const handleSearchKey = (e) => {
    if (e.key === "Enter") loadUsers();
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete account ${u.email}? This removes all their retainers and data. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("Account deleted");
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const handleResendWelcome = async (u) => {
    if (!window.confirm(`Resend the welcome email to ${u.email}?`)) return;
    setResendingUserId(u.user_id);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/resend-welcome`);
      if (r.data?.ok) {
        toast.success(`Welcome email resent to ${u.email}`);
      } else {
        const reason = r.data?.reason || "unknown error";
        toast.error(`Email not sent: ${reason}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend welcome email");
    } finally {
      setResendingUserId(null);
    }
  };

  if (authLoading || authorized === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F6F1] text-sm text-[#69747C]">
        Checking access.
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8F6F1] text-center px-6" data-testid="admin-denied">
        <Shield className="w-10 h-10 text-[#B89B5E] mb-4" />
        <h1 className="font-display text-2xl font-semibold text-[#1F2326]">Admin only</h1>
        <p className="text-sm text-[#69747C] mt-2 max-w-md">
          This area is restricted to RetainerTracker administrators.
        </p>
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="mt-6 bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-4 py-2 rounded-lg font-medium text-sm transition-colors"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6F1]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1F2326] text-[#B89B5E] text-[10px] tracking-[0.1em] uppercase font-semibold mb-2">
              <Shield className="w-3 h-3" /> Admin
            </div>
            <h1 className="font-display text-4xl font-semibold tracking-tighter text-[#1F2326]">
              Operations
            </h1>
            <p className="text-sm text-[#69747C] mt-2">
              Signed in as {user?.email}. Manage users, plans, and analytics.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="text-sm text-[#69747C] hover:text-[#1F2326]"
          >
            Exit admin →
          </button>
        </div>

        {analytics?.totals ? (
          <div
            data-testid="admin-subscribers-widget"
            className="inline-flex items-center gap-3 bg-white border border-[#E4DFD2] rounded-full px-4 py-2 mb-6 text-xs text-[#69747C]"
          >
            <span className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#1F2326]">
              Subscribers
            </span>
            <span className="text-[#E4DFD2]">·</span>
            <span data-testid="admin-active-count">
              <span className="font-semibold text-[#1F2326] tabular-nums">
                {analytics.totals.active ?? "—"}
              </span>{" "}
              active
            </span>
            <span className="text-[#E4DFD2]">·</span>
            <span data-testid="admin-paying-count">
              <span className="font-semibold text-[#42624B] tabular-nums">
                {analytics.totals.paid ?? "—"}
              </span>{" "}
              paying
            </span>
          </div>
        ) : null}

        <div className="flex items-center gap-1 mb-6 border-b border-[#E4DFD2]">
          {[
            { id: "users", label: "Users", icon: Users },
            { id: "analytics", label: "Analytics", icon: Activity },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${
                tab === t.id
                  ? "border-[#1F2326] text-[#1F2326]"
                  : "border-transparent text-[#69747C] hover:text-[#1F2326]"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <div data-testid="admin-users-tab">
            <div className="flex items-center gap-3 mb-5">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 text-[#69747C] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="admin-search"
                  className={`${inputCls} pl-9`}
                  placeholder="Search by email or company name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleSearchKey}
                />
              </div>
              <button
                type="button"
                onClick={loadUsers}
                data-testid="admin-search-btn"
                className="bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-4 py-2 rounded-lg text-sm font-medium"
              >
                Search
              </button>
              <div className="ml-auto text-xs text-[#69747C]">
                {users.length} {users.length === 1 ? "user" : "users"}
              </div>
            </div>

            <div className="bg-white border border-[#E4DFD2] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead>
                    <tr className="text-[11px] tracking-wider uppercase text-[#69747C] bg-[#FBFAF6]/60">
                      <th className="text-left font-semibold py-3 px-5">User</th>
                      <th className="text-left font-semibold py-3 px-3">Company</th>
                      <th className="text-left font-semibold py-3 px-3">Plan</th>
                      <th className="text-right font-semibold py-3 px-3">Retainers</th>
                      <th className="text-left font-semibold py-3 px-3">Status</th>
                      <th className="text-left font-semibold py-3 px-3">Signed up</th>
                      <th className="text-left font-semibold py-3 px-3">Last active</th>
                      <th className="py-3 px-5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingUsers && (
                      <tr>
                        <td colSpan={8} className="px-5 py-10 text-center text-[#69747C]">
                          Loading users.
                        </td>
                      </tr>
                    )}
                    {!loadingUsers && users.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-5 py-10 text-center text-[#69747C]">
                          No users found.
                        </td>
                      </tr>
                    )}
                    {!loadingUsers &&
                      users.map((u) => (
                        <tr
                          key={u.user_id}
                          className="border-t border-[#EDE7D6] hover:bg-[#FBFAF6]/60 transition-colors"
                          data-testid={`admin-user-row-${u.user_id}`}
                        >
                          <td className="px-5 py-3">
                            <div className="font-medium text-[#1F2326]">{u.email}</div>
                            <div className="text-xs text-[#69747C]">{u.name || "—"}</div>
                          </td>
                          <td className="px-3 py-3 text-[#1F2326]">{u.company_name || "—"}</td>
                          <td className="px-3 py-3">
                            <div className="font-medium text-[#1F2326]">{u.plan_name || "Trial"}</div>
                            <div className="text-xs text-[#69747C]">cap {u.max_retainers}</div>
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums text-[#1F2326]">
                            {u.retainer_count}
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${
                                statusTone[u.status] || statusTone.inactive
                              }`}
                            >
                              {u.status.replace("_", " ")}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-xs text-[#69747C]">{fmtDate(u.signup_date)}</td>
                          <td className="px-3 py-3 text-xs text-[#69747C]">{fmtDate(u.last_active_at)}</td>
                          <td className="px-5 py-3 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleResendWelcome(u)}
                                disabled={resendingUserId === u.user_id}
                                data-testid={`admin-resend-welcome-${u.user_id}`}
                                className="text-[#69747C] hover:text-[#B89B5E] p-1.5 rounded-md hover:bg-[#F3EFE3] disabled:opacity-50"
                                title="Resend welcome email"
                              >
                                <Mail className={`w-4 h-4 ${resendingUserId === u.user_id ? "animate-pulse" : ""}`} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditing(u)}
                                data-testid={`admin-edit-${u.user_id}`}
                                className="text-[#69747C] hover:text-[#1F2326] p-1.5 rounded-md hover:bg-[#F3EFE3]"
                                title="Edit"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(u)}
                                data-testid={`admin-delete-${u.user_id}`}
                                className="text-[#7E332D] hover:text-[#C76055] p-1.5 rounded-md hover:bg-[#FAEEED]"
                                title="Delete account"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "analytics" && (
          <div data-testid="admin-analytics-tab" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border border-[#E4DFD2] rounded-xl p-5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#69747C]">Users</div>
                <div className="font-display text-3xl font-semibold text-[#1F2326] mt-1">
                  {analytics?.totals?.users ?? "—"}
                </div>
              </div>
              <div className="bg-white border border-[#E4DFD2] rounded-xl p-5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#69747C]">Paid</div>
                <div className="font-display text-3xl font-semibold text-[#42624B] mt-1">
                  {analytics?.totals?.paid ?? "—"}
                </div>
              </div>
              <div className="bg-white border border-[#E4DFD2] rounded-xl p-5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#69747C]">Events tracked</div>
                <div className="font-display text-3xl font-semibold text-[#1F2326] mt-1">
                  {analytics?.counts?.reduce((s, c) => s + c.count, 0) ?? "—"}
                </div>
              </div>
              <div className="bg-white border border-[#E4DFD2] rounded-xl p-5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#69747C]">Event types</div>
                <div className="font-display text-3xl font-semibold text-[#1F2326] mt-1">
                  {analytics?.counts?.length ?? "—"}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-[#E4DFD2] rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-[#EDE7D6] flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#B89B5E]" />
                  <div className="font-display text-base font-semibold text-[#1F2326]">Event counts</div>
                </div>
                <div className="divide-y divide-[#EDE7D6]">
                  {(analytics?.counts || []).length === 0 && (
                    <div className="px-5 py-6 text-sm text-[#69747C]">No events yet.</div>
                  )}
                  {(analytics?.counts || []).map((c) => (
                    <div key={c.event} className="flex items-center justify-between px-5 py-2.5 text-sm">
                      <span className="text-[#1F2326]">{c.event}</span>
                      <span className="tabular-nums text-[#69747C]">{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-[#E4DFD2] rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-[#EDE7D6] flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#B89B5E]" />
                  <div className="font-display text-base font-semibold text-[#1F2326]">Recent events</div>
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-[#EDE7D6]">
                  {(analytics?.recent || []).length === 0 && (
                    <div className="px-5 py-6 text-sm text-[#69747C]">No events yet.</div>
                  )}
                  {(analytics?.recent || []).map((e) => (
                    <div key={e.id} className="px-5 py-2.5 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#1F2326]">{e.event}</span>
                        <span className="text-[#69747C]">{fmtDate(e.created_at)}</span>
                      </div>
                      <div className="text-[#69747C] mt-0.5 truncate">
                        {e.email || e.anon_id || "anon"} · {e.path || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <EditUserDialog
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          loadUsers();
        }}
      />
    </div>
  );
};

const EditUserDialog = ({ user, onClose, onSaved }) => {
  const [planId, setPlanId] = useState("");
  const [override, setOverride] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setPlanId(user.plan_id || "");
      setOverride(user.max_retainers ?? "");
      setStatus(user.status === "trial_expired" ? "trialing" : (user.status === "paid" ? "active" : user.status) || "");
    }
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const payload = {};
      if (planId) payload.plan_id = planId;
      if (override !== "" && override !== null) payload.max_retainers_override = parseInt(override, 10);
      if (status) payload.status = status;
      await api.patch(`/admin/users/${user.user_id}`, payload);
      toast.success("User updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#F8F6F1] border border-[#E4DFD2] max-w-md" data-testid="admin-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Edit account</DialogTitle>
        </DialogHeader>
        {user ? (
          <div className="space-y-4">
            <div className="text-sm text-[#1F2326]">
              <div className="font-medium">{user.email}</div>
              <div className="text-xs text-[#69747C]">{user.user_id}</div>
            </div>

            <button
              type="button"
              data-testid="admin-comp-preset"
              onClick={() => {
                setPlanId("agency");
                setOverride(40);
                setStatus("active");
              }}
              className="w-full text-left bg-[#EFF3EE] border border-[#C5D5C8] text-[#42624B] rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-[#E2EBE2] transition-colors"
            >
              Comp this account · Agency plan, full access, no Stripe charge
              <div className="text-[11px] text-[#69747C] font-normal mt-0.5">
                For QA, friends & family, partners. Sets plan + status manually.
              </div>
            </button>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-[#1F2326]">Plan</label>
              <Select value={planId} onValueChange={setPlanId}>
                <SelectTrigger data-testid="admin-edit-plan" className="bg-white border-[#E4DFD2]">
                  <SelectValue placeholder="No plan (trial)" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="solo">Solo · $19 · 5 retainers</SelectItem>
                  <SelectItem value="studio">Studio · $49 · 15 retainers</SelectItem>
                  <SelectItem value="agency">Agency · $99 · 40 retainers</SelectItem>
                  <SelectItem value="scale">Scale · $179 · 100 retainers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-[#1F2326]">Active retainer limit override</label>
              <input
                data-testid="admin-edit-override"
                type="number"
                min="0"
                className={inputCls}
                value={override}
                onChange={(e) => setOverride(e.target.value)}
              />
              <div className="text-[11px] text-[#69747C]">Overrides the plan default. Hard cap on creating new retainers.</div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-[#1F2326]">Account status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="admin-edit-status" className="bg-white border-[#E4DFD2]">
                  <SelectValue placeholder="Set status" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="active">Active (paid)</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[#69747C] hover:text-[#1F2326]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-edit-save"
            className="bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] disabled:opacity-60 px-4 py-2 rounded-lg text-sm font-medium"
          >
            {saving ? "Saving." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Admin;
